// FILE: src/components/ReadinessHero.js
//
// The readiness "mission-control" card: big progress ring + status pill,
// trend headline, Next Best Action panel with Start Session CTA, and the
// six weighted sub-score bars on the right.
//
// All data wires to existing services:
//   - readiness         (readinessService.computeReadiness)
//   - trend             (weeklyTrendService.computeWeeklyTrend on snapshots)
//   - todaysActivity    (studyPlanService.pickTodaysActivity)

import React from 'react';
import DashCard from '../dashboard/DashCard';
import ProgressRing from '../shared/ProgressRing';
import styles from './ReadinessHero.module.css';
import { STATUS_LABEL } from '../../services/readinessService';

const SUB_ICONS = {
  rolling_correctness:    '🎯',
  phase_coverage:         '🧭',
  full_sim_count:         '📋',
  weak_area_improvement:  '🌱',
  recency:                '⚡',
  time_management:        '⏱',
};

const SUB_LABEL = {
  rolling_correctness:    'Accuracy',
  phase_coverage:         'Coverage',
  full_sim_count:         'Sims',
  weak_area_improvement:  'Weak Areas',
  recency:                'Recency',
  time_management:        'Time Mgmt',
};

const SUB_ORDER = [
  'rolling_correctness',
  'phase_coverage',
  'full_sim_count',
  'weak_area_improvement',
  'recency',
  'time_management',
];

export default function ReadinessHero({
  examLabel,
  readiness,
  trend,
  todaysActivity,
  onSeeBreakdown,
  onStartSession,
}) {
  const score   = typeof readiness?.score === 'number' ? readiness.score : null;
  const status  = readiness?.status || 'not_started';
  const label   = STATUS_LABEL[status] || 'Getting started';
  const inputs  = readiness?.inputs || {};

  const trendValid = trend && trend.sufficient && typeof trend.delta === 'number';
  const trendUp    = trendValid && trend.direction === 'up';
  const trendDown  = trendValid && trend.direction === 'down';

  return (
    <DashCard
      eyebrow={<>{(examLabel || 'Your').split(/([®™])/).map((p, i) => /[®™]/.test(p) ? <span key={i} className={styles.regMark}>{p}</span> : p)} Readiness</>}
      action={
        <button type="button" onClick={onSeeBreakdown} data-testid="dashboard-readiness-link">
          View full readiness breakdown ›
        </button>
      }
      testId="dashboard-readiness"
    >
      <div className={styles.row}>

        {/* ── Left: ring + status pill ── */}
        <div className={styles.ringCol}>
          <ProgressRing value={score || 0} size={200} stroke={14}>
            <div className={styles.scoreNum}>{score != null ? score : '—'}</div>
            <div className={styles.scoreDenom}>/100</div>
          </ProgressRing>
          <span className={`${styles.pill} ${pillClass(status, styles)}`}>{label}</span>
        </div>

        {/* ── Middle: headline + trend + Next Best Action ── */}
        <div className={styles.midCol}>
          {trendValid ? (
            <>
              <h2 className={styles.headline}>{headlineFromTrend(trend, score)}</h2>
              <p className={styles.lede}>{ledeFromTrend(trend)}</p>
              <div className={`${styles.trend} ${trendUp ? styles.trendUp : ''} ${trendDown ? styles.trendDown : ''}`}>
                {trendUp ? '↗' : trendDown ? '↘' : '→'} {trendUp ? 'Trending up' : trendDown ? 'Trending down' : 'Steady'}
                <span className={styles.delta}>
                  {trend.delta > 0 ? '+' : ''}{trend.delta} pts vs last week
                </span>
              </div>
            </>
          ) : (
            <>
              <h2 className={styles.headline}>{score != null && score > 0 ? "You're building momentum." : 'Ready when you are.'}</h2>
              <p className={styles.lede}>A few more sessions and we'll start showing your weekly trend.</p>
            </>
          )}

          {todaysActivity && (
            <div className={styles.nextBest}>
              <div className={styles.nextBestLabel}>◎ Next Best Action</div>
              <div className={styles.nextBestTitle}>{actionTitle(todaysActivity)}</div>
              <div className={styles.nextBestSub}>{actionSub(todaysActivity)}</div>
              <button
                type="button"
                className={styles.nextBestBtn}
                onClick={onStartSession}
                data-testid="dashboard-start-next"
              >Start Session</button>
            </div>
          )}
        </div>

        {/* ── Right: six weighted sub-scores ── */}
        <div className={styles.subCol}>
          {SUB_ORDER.map((key) => {
            const item = inputs[key];
            if (!item) return null;
            const val = Math.round(item.value || 0);
            const wt  = Math.round((item.weight || 0) * 100);
            return (
              <div className={styles.subRow} key={key}>
                <div className={styles.subHead}>
                  <span className={styles.subIcon}>{SUB_ICONS[key]}</span>
                  <span className={styles.subName}>{SUB_LABEL[key]}</span>
                  <span className={styles.subWeight}>({wt}%)</span>
                  <span className={styles.subVal}>{val}/100</span>
                </div>
                <div className={styles.subBar}>
                  <span
                    className={barTone(val, styles)}
                    style={{ width: `${val}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashCard>
  );
}

function pillClass(status, styles) {
  switch (status) {
    case 'exam_ready':         return styles.pillReady;
    case 'getting_close':      return styles.pillClose;
    case 'building_foundation':return styles.pillBuilding;
    default:                   return styles.pillStart;
  }
}

function barTone(val, styles) {
  if (val >= 85) return styles.barGood;
  if (val < 50)  return styles.barAmber;
  return styles.barBrand;
}

function headlineFromTrend(trend, score) {
  if (trend.direction === 'up') return `Up ${trend.delta} points this week.`;
  if (trend.direction === 'down') return `Down ${Math.abs(trend.delta)} points this week.`;
  return score != null && score >= 75 ? "You're in the target zone." : "Steady — keep going.";
}

function ledeFromTrend(trend) {
  if (trend.direction === 'up') return 'Keep the momentum going!';
  if (trend.direction === 'down') return 'A focused session this week will turn it around.';
  return 'Consistent practice beats sprints.';
}

function actionTitle(a) {
  if (a?.description) return a.description.split(' — ')[0] || a.description;
  const t = (a?.activity_type || '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1) + ' session';
}

function actionSub(a) {
  const bits = [];
  if (a?.focus_phase) bits.push(a.focus_phase);
  if (typeof a?.recommended_count === 'number') bits.push(`${a.recommended_count} questions`);
  return bits.join(' • ') || 'Pick up where you left off';
}
